import { memo, useCallback, useMemo } from 'react'
import {
  CrewAvatarSize,
  CrewAvatar,
} from 'components/elements/crewAvatar/crewAvatar'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'
import { useAppSelector } from 'states/hooks'
import { GetUsersRequest } from '@crew/apis/lookup/models/getUsers/request'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetLookupUsersQuery } from '@crew/apis/lookup/lookupApis'
import classNames from 'classnames'
import { CrewCheckBox } from 'components/devextreme/crewCheckBox'
import { UserRef } from '@crew/models/refs'

export type ChatStartUserTabGroupListProps = {
  onUserSelected: (userId: string, checked: boolean) => void
  isLoading: boolean
  selectedUserIds: string[]
}

type User = UserRef & {
  isSelected: boolean
}

// グループタブのユーザー一覧
export const ChatStartGroupTabUserList = memo(
  (props: ChatStartUserTabGroupListProps) => {
    const keyword = useAppSelector(
      (state) => state.chatStartEntryDialog.groupTabSearchKeyword
    )

    const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

    // キーワードに該当するユーザ一覧取得
    // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
    // undefined時はskipTokenでAPIを実行しない（キーワードテキストが空のとき）
    const getUsersParams: GetUsersRequest | undefined = keyword
      ? {
          keyword: keyword,
          userId: undefined,
        }
      : undefined

    const { data: getLookupUsersQueryResult } = useGetLookupUsersQuery(
      getUsersParams ?? skipToken
    )

    // 検索したユーザからログインユーザを除外し、選択状態を付与したユーザリストを作成
    const users: User[] = useMemo(() => {
      const searchedUsers = getLookupUsersQueryResult?.users ?? []

      return Object.values(searchedUsers)
        .filter((user) => user.id !== loggedInUser?.id) // ログインユーザを除外
        .map((user) => ({
          ...user,
          // 選択状態を設定
          isSelected: props.selectedUserIds.includes(user.id),
        }))
    }, [
      getLookupUsersQueryResult?.users,
      loggedInUser?.id,
      props.selectedUserIds,
    ])

    // ユーザー選択のチェックボックス変更時
    const handleCheckboxChange = useCallback(
      (userId: string, checked: boolean) => {
        // 親コンポーネントに選択状態変更を通知
        props.onUserSelected(userId, checked)
      },
      [props]
    )

    // チェックボックスクリック時
    const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
      // 親コンポーネントとチェックボックスでクリック時のイベントが重複してしまい選択状態が正しく動作しなくなるため、
      // 親コンポーネントのクリックイベントが伝播しないようにする
      e.stopPropagation()
    }, [])

    return (
      <div className="h-full flex flex-col py-2">
        {users.length > 0 &&
          Object.values(users).map((user) => {
            return (
              <div
                key={user.id} // Warning表示回避のkey指定
                className={classNames(
                  'flex items-center gap-2 py-1 cursor-pointer w-[390px] border-b crew-border-gray',
                  props.isLoading && 'pointer-events-none opacity-[0.5]' // API実行中はクリックイベントを無効化
                )}
                onClick={() => handleCheckboxChange(user.id, !user.isSelected)}
              >
                {/* ユーザー選択チェックボックス */}
                <div onClick={handleCheckboxClick}>
                  <CrewCheckBox
                    value={user.isSelected}
                    onValueChanged={(e) =>
                      handleCheckboxChange(user.id, e.value ?? false)
                    }
                  />
                </div>
                {/* ユーザーアバター */}
                <CrewAvatar
                  displayName={user.displayName}
                  imageURL={generateImageAvatarUrl(EntityType.User, user.id)}
                  size={CrewAvatarSize.xs}
                  cacheValue={user.id + user.version}
                />
                {/* ユーザー名 */}
                <span className="text-crew-blue-3-light dark:text-crew-blue-2-dark max-w-xl truncate whitespace-nowrap overflow-hidden">
                  {user.displayName}
                </span>
              </div>
            )
          })}
      </div>
    )
  }
)
